/**
 * Node.js Runtime WASM Loader for AgentShield
 * 
 * This loader uses fs.readFileSync to load WASM in Node.js runtime.
 * It provides full cryptographic verification capabilities.
 */

import fs from 'fs';
import path from 'path';
import { setWasmModule } from '@kya-os/agentshield';

let wasmInitialized = false;
let wasmModule: WebAssembly.Module | null = null;

/**
 * Load WASM module using Node.js fs module
 * This only works in Node.js runtime, not Edge Runtime
 */
export async function loadWasmNodejs(): Promise<boolean> {
  if (wasmInitialized) {
    return true;
  }

  try {
    // Try multiple possible WASM locations
    const possiblePaths = [
      // In node_modules (most likely)
      path.join(process.cwd(), 'node_modules', '@kya-os', 'agentshield', 'dist', 'wasm', 'agentshield_wasm_bg.wasm'),
      // In project root (if user copied it)
      path.join(process.cwd(), 'agentshield_wasm_bg.wasm'),
      // Relative to current file
      path.join(__dirname, '..', '..', '..', 'agentshield', 'dist', 'wasm', 'agentshield_wasm_bg.wasm'),
    ];

    let wasmBuffer: Buffer | null = null;
    let loadedPath: string | null = null;

    for (const wasmPath of possiblePaths) {
      try {
        if (fs.existsSync(wasmPath)) {
          wasmBuffer = fs.readFileSync(wasmPath);
          loadedPath = wasmPath;
          break;
        }
      } catch (e) {
        // Try next path
        continue;
      }
    }

    if (!wasmBuffer) {
      console.warn('AgentShield: WASM file not found in any expected location');
      return false;
    }

    // Convert Buffer to Uint8Array for WebAssembly
    // This is the proper way to handle Buffer -> ArrayBuffer conversion
    const bytes = new Uint8Array(wasmBuffer);
    wasmModule = await WebAssembly.compile(bytes);
    
    // Set the module in AgentShield
    setWasmModule(wasmModule);
    
    wasmInitialized = true;
    console.log(`✅ AgentShield: WASM loaded successfully from ${loadedPath} (Node.js runtime)`);
    console.log('🔐 Cryptographic verification enabled (95-100% confidence)');
    
    return true;
  } catch (error) {
    console.warn('⚠️ AgentShield: Failed to load WASM in Node.js runtime:', error);
    console.log('📊 Falling back to pattern detection (85% confidence)');
    return false;
  }
}

/**
 * Check if we're in Node.js runtime
 */
export function isNodejsRuntime(): boolean {
  return typeof process !== 'undefined' && 
         typeof process.versions !== 'undefined' && 
         typeof process.versions.node !== 'undefined' &&
         typeof require !== 'undefined';
}

/**
 * Get the loaded WASM module
 */
export function getWasmModule(): WebAssembly.Module | null {
  return wasmModule;
}

/**
 * Check if WASM is initialized
 */
export function isWasmInitialized(): boolean {
  return wasmInitialized;
}